import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const PYRAMID = {
  top: {
    label: "Top Notes",
    subtitle: "First 15 minutes — the opening statement.",
    notes: [
      { name: "Calabrian Bergamot", desc: "Effervescent citrus with a bittersweet edge — the signature top note of Aventus." },
      { name: "Saffron", desc: "Warm, leathery spice from Kashmir. Instantly luxurious." },
      { name: "Pink Pepper", desc: "Rose-tinged pepper — sparkling, feminine, effervescent." },
      { name: "Sicilian Lemon", desc: "Sun-drenched, sharper than Meyer. Classic cologne opener." },
    ],
  },
  heart: {
    label: "Heart Notes",
    subtitle: "After 1–4 hours — the soul of the fragrance.",
    notes: [
      { name: "Damask Rose", desc: "The most cinematic rose — lush, honeyed, faintly berry." },
      { name: "Iris Butter", desc: "Powdery, cool, austerely elegant. The couturier's note." },
      { name: "Coffee Absolute", desc: "Roasted, bitter, seductive — lifts gourmand blends." },
      { name: "Jasmine Sambac", desc: "Indolic, tropical, sensuous. The night-blooming muse." },
    ],
  },
  base: {
    label: "Base Notes",
    subtitle: "Hours 4+ — the trail, the memory, the skin.",
    notes: [
      { name: "Cambodian Oud", desc: "Smoky, resinous, sacred. The rarest wood note in perfumery." },
      { name: "Madagascar Vanilla", desc: "Creamy, rounded, warm. Not sweet — velvety." },
      { name: "Ambergris", desc: "Salty, marine, radiant. Wraps everything in golden light." },
      { name: "Vetiver Haiti", desc: "Earthy, grassy, faintly smoked. Anchors any composition." },
    ],
  },
};

export function OlfactoryPyramid() {
  const [active, setActive] = useState(null);
  const tiers = [
    { key: "top", width: "w-[64%]" },
    { key: "heart", width: "w-[82%]" },
    { key: "base", width: "w-full" },
  ];

  return (
    <div data-testid="olfactory-pyramid" className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-3">The Olfactory Pyramid</div>
        <h2 className="font-serif text-3xl md:text-4xl text-[#1A1918] leading-[1.1]">
          Every scent has three acts.
        </h2>
        <p className="mt-5 text-[#1A1918]/75 leading-relaxed max-w-md">
          Hover any layer to explore the ingredient stories behind Scentodore’s hand-blended compositions — from luminous top notes to grounding base resins.
        </p>
        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active.name}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 p-5 rounded-xl border border-[#C5A059]/40 bg-[#FAF8F5]"
              data-testid="pyramid-tooltip"
            >
              <div className="font-serif text-xl text-[#1A1918]">{active.name}</div>
              <div className="mt-2 text-sm text-[#1A1918]/75 leading-relaxed">{active.desc}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex flex-col items-center gap-3 justify-center">
        {tiers.map((tier, idx) => {
          const t = PYRAMID[tier.key];
          return (
            <motion.div
              key={tier.key}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 * idx, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
              className={`${tier.width} rounded-xl border border-[#C5A059]/30 bg-gradient-to-b from-[#FAF8F5] to-[#F1ECE4] p-4 md:p-5`}
              data-testid={`pyramid-tier-${tier.key}`}
            >
              <div className="flex items-baseline justify-between">
                <div className="font-serif text-lg text-[#1A1918]">{t.label}</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#1A1918]/60">{t.subtitle}</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {t.notes.map((n) => (
                  <button
                    key={n.name}
                    onMouseEnter={() => setActive(n)}
                    onFocus={() => setActive(n)}
                    onMouseLeave={() => setActive(null)}
                    onBlur={() => setActive(null)}
                    data-testid={`pyramid-note-${n.name.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      active?.name === n.name
                        ? "bg-[#C5A059]/20 border-[#C5A059] text-[#1A1918]"
                        : "bg-[#FAF8F5] border-black/10 text-[#1A1918]/80 hover:border-[#C5A059]/40 hover:bg-[#C5A059]/8"
                    }`}
                  >
                    {n.name}
                  </button>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default OlfactoryPyramid;
